'use client'

import { isNull, isUndefined } from 'lodash-es'
import { useCallback, useEffect } from 'react'

import {
  defaultFilters,
  MAX_USING_FEE,
  MIN_USING_FEE,
  useMapStore,
  useSearchFilterStore,
  useViewingRoomStore,
} from '@/entities/map'
import { eventManager, mapManager, TCoordinates } from '@/shared/lib'

import { TFilterOptions, useSearchMapBridge } from './bridge'

type Props = {
  mapInitialized: boolean
}

const convertUsingFee = ({
  usingFee,
  defaultValue,
}: {
  usingFee?: number | null
  defaultValue: number
}) => {
  if (isUndefined(usingFee)) return
  if (isNull(usingFee)) return null
  if (usingFee === defaultValue) return null

  return usingFee
}

export const Webview = ({ mapInitialized }: Props) => {
  const bridge = useSearchMapBridge()
  const mapActions = useMapStore(state => state.actions)
  const filterActions = useSearchFilterStore(state => state.actions)

  const updateMap = useCallback(
    ({
      coordinates,
      zoomLevel,
    }: {
      coordinates?: TCoordinates
      zoomLevel?: number
    }) => {
      if (isUndefined(coordinates)) return

      mapManager.changeMapCenter({
        lat: coordinates.lat,
        lng: coordinates.lng,
        zoomLevel: zoomLevel ?? null,
      })
      mapActions.updateCenter(coordinates)
      eventManager.triggerEvent('update')
    },
    [mapActions],
  )

  const updateFilter = useCallback(
    (filter?: {
      startDate?: string
      endDate?: string
      options?: TFilterOptions
    }) => {
      if (isUndefined(filter)) return

      const currentFilters = useSearchFilterStore.getState().filters

      const minUsingFee = convertUsingFee({
        usingFee: filter?.options?.minUsingFee,
        defaultValue: MIN_USING_FEE,
      })
      const maxUsingFee = convertUsingFee({
        usingFee: filter?.options?.maxUsingFee,
        defaultValue: MAX_USING_FEE,
      })

      const updatedFilter = (() => {
        if (filter.startDate && filter.endDate) {
          return {
            ...currentFilters,
            startDate: filter.startDate,
            endDate: filter.endDate,
          }
        }

        return {
          ...defaultFilters,
          startDate: currentFilters.startDate,
          endDate: currentFilters.endDate,
          ...filter.options,
          ...(isUndefined(minUsingFee) ? {} : { minUsingFee }),
          ...(isUndefined(maxUsingFee) ? {} : { maxUsingFee }),
        }
      })()

      filterActions.update(updatedFilter)
    },
    [filterActions],
  )

  useEffect(() => {
    const mapUnsubscribe = useMapStore.subscribe(
      state => state.params,
      params => {
        bridge.updateViewport(params)
      },
    )

    const viewingRoomUnsubscribe = useViewingRoomStore.subscribe(
      state => state.viewingRoom,
      viewingRoom => {
        bridge.updateRoomMarker(
          viewingRoom?.position ?? { lat: null, lng: null },
        )
      },
    )

    return () => {
      mapUnsubscribe()
      viewingRoomUnsubscribe()
    }
  }, [bridge])

  useEffect(() => {
    if (!bridge) {
      return
    }

    const filterUnsubscribe = bridge.onFilterChange(
      ({ coordinates, zoomLevel, startDate, endDate, options }) => {
        updateMap({ coordinates, zoomLevel })
        updateFilter({ startDate, endDate, options })
      },
    )

    return () => {
      filterUnsubscribe?.()
    }
  }, [bridge, updateFilter, updateMap])

  useEffect(() => {
    if (!bridge || !mapInitialized) {
      return
    }

    eventManager.addEventListener('dragstart', bridge.notifyDragStart)
    eventManager.addEventListener('dragend', bridge.notifyDragEnd)
  }, [bridge, mapInitialized])

  return <></>
}
